import { NotificationsOptions, useNotification as useNotificationFn } from '@kyvg/vue3-notification';
import { AxiosError } from 'axios';
import { MonoTypeOperatorFunction, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { useI18n } from 'vue-i18n';

import { UiLevel } from '@silae/helpers';
import { httpErrorMsgFactory } from '~/utils';

export type NotificationMsg = {
	title: string;
	text: string;
	type?: UiLevel;
};

//
function getMsg<T>(data: T, msgFactory: string | ((data: T) => string)): string {
	if (typeof msgFactory === 'function') {
		return msgFactory(data);
	} else {
		return msgFactory;
	}
}

export function useNotification() {
	const { notify } = useNotificationFn();
	const { t } = useI18n();
	const notifyError = (notif: NotificationMsg, options: NotificationsOptions = {}) =>
		notify({ ...notif, type: UiLevel.ERROR, ...options });
	const notifySuccess = (notif: NotificationMsg, options: NotificationsOptions = {}) =>
		notify({ ...notif, type: UiLevel.SUCCESS, ...options });
	const notifyWarning = (notif: NotificationMsg, options: NotificationsOptions = {}) =>
		notify({ ...notif, type: UiLevel.WARNING, ...options });
	const notifyInfo = (notif: NotificationMsg, options: NotificationsOptions = {}) => notify({ ...notif, type: UiLevel.INFO, ...options });

	const notifyResult = <T, E extends Error = Error>(
		success: string | ((data: T) => string),
		error: string | ((err: E) => string)
	): MonoTypeOperatorFunction<T> => {
		return (source: Observable<T>): Observable<T> => {
			return source.pipe(
				map(value => {
					const msg = getMsg(value, success);
					notifySuccess({
						title: t('commons.success'),
						text: msg
					});
					return value;
				}),
				catchError(err => {
					const msg = getMsg(err, error);
					notifyError({
						title: t('commons.error'),
						text: msg
					});
					return throwError(() => err);
				})
			);
		};
	};

	const catchAndNotifyError = <T>(
		message: string | ((err: AxiosError) => string) = (err: AxiosError) => t(httpErrorMsgFactory(err))
	): MonoTypeOperatorFunction<T> => {
		return catchError((err: AxiosError) => {
			const msg = getMsg(err, message);
			notifyError({
				title: t('commons.error'),
				text: msg
			});
			return throwError(() => err);
		});
	};

	return {
		catchAndNotifyError,
		notifyError,
		notifyInfo,
		notifyResult,
		notifySuccess,
		notifyWarning
	};
}
